import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, MessageSquare } from 'lucide-react';
import { APP_VERSION } from '@/config/version';

interface FullPageErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  showReportButton?: boolean;
}

export function FullPageErrorFallback({ 
  error, 
  onReset, 
  showReportButton = true 
}: FullPageErrorFallbackProps) {
  const handleReport = () => {
    // Navigate to feedback form with error context
    const errorMessage = error?.message || 'Unknown error';
    const feedbackUrl = `/account?feedback=error&context=${encodeURIComponent(errorMessage)}`;
    window.location.href = feedbackUrl;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <Card className="max-w-md w-full text-center">
        <CardHeader className="pb-4">
          <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-4">
            <AlertTriangle className="w-6 h-6 text-destructive" />
          </div>
          <CardTitle className="text-xl">Something went wrong</CardTitle>
          <CardDescription className="mt-2">
            We're sorry, but something unexpected happened. Please try reloading the app.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-3">
          <Button onClick={onReset} className="w-full gap-2">
            <RefreshCw className="w-4 h-4" />
            Reload App
          </Button>
          
          {showReportButton && (
            <Button 
              variant="outline" 
              onClick={handleReport} 
              className="w-full gap-2"
            >
              <MessageSquare className="w-4 h-4" />
              Report Issue
            </Button>
          )}
          
          {error && process.env.NODE_ENV === 'development' && (
            <div className="mt-4 p-3 rounded-md bg-muted text-left">
              <p className="text-xs font-mono text-muted-foreground break-all">
                {error.message}
              </p>
            </div>
          )}
          
          <p className="text-xs text-muted-foreground pt-2">
            Version {APP_VERSION}
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

interface InlineErrorFallbackProps {
  error: Error | null;
  onReset: () => void;
  height?: string | number;
  message?: string;
  retriesRemaining?: number;
}

export function InlineErrorFallback({
  error,
  onReset,
  height = 200,
  message = 'This section failed to load',
  retriesRemaining,
}: InlineErrorFallbackProps) {
  const heightStyle = typeof height === 'number' ? `${height}px` : height;
  const canRetry = retriesRemaining === undefined || retriesRemaining > 0;

  return (
    <div 
      className="flex flex-col items-center justify-center gap-3 p-4 rounded-lg border border-border bg-muted/30"
      style={{ minHeight: heightStyle }}
    >
      <AlertTriangle className="w-8 h-8 text-muted-foreground" />
      <p className="text-sm text-muted-foreground text-center">{message}</p>
      
      {canRetry ? (
        <Button variant="outline" size="sm" onClick={onReset} className="gap-2">
          <RefreshCw className="w-3 h-3" />
          Try Again
        </Button>
      ) : (
        <p className="text-xs text-muted-foreground">
          This feature is temporarily unavailable
        </p>
      )}
      
      {error && process.env.NODE_ENV === 'development' && (
        <p className="text-xs font-mono text-muted-foreground max-w-full truncate">
          {error.message}
        </p>
      )}
    </div>
  );
}
